'use client'

// Sticky section tracker — bottom-right chip that reports the dominant
// section in the viewport. Visual design is LOCKED; only the text
// content updates, with a width-preserving crossfade between sections.
// Particle trail emits on downward scroll velocity (purely additive).

import { useEffect, useRef, useState, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from '@/lib/animations/gsapSetup'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'

type SectionKey =
  | 'surface' | 'wound' | 'restoration'
  | 'recognized' | 'field' | 'invitation'

// Section ID → tracker state. The transition-bridge deliberately has no
// entry — the tracker keeps showing 'restoration' throughout it.
const SECTION_MAP: { id: string; key: SectionKey; num: number }[] = [
  { id: 'hero',      key: 'surface',     num: 0 },
  { id: 'problem',   key: 'wound',       num: 1 },
  { id: 'solution',  key: 'restoration', num: 2 },
  { id: 'awards',    key: 'recognized',  num: 3 },
  { id: 'interlude', key: 'field',       num: 4 },
  { id: 'contact',   key: 'invitation',  num: 5 },
]

const numForKey = (k: SectionKey) =>
  SECTION_MAP.find(s => s.key === k)?.num ?? 0

const pad = (n: number) => String(n).padStart(2, '0')

const a11yKey = (k: SectionKey) => `a11y${k.charAt(0).toUpperCase()}${k.slice(1)}`

// ── Particle constants ─────────────────────────────────────────────────────
const POOL_SIZE          = 30
const VEL_DAMPING        = 0.92    // applied each frame
const VEL_FAST_BLUR      = 2.0     // px/ms — turn on motion blur
const VEL_BLUR_RELEASE   = 1.0     // px/ms — turn it off
const VEL_FAST_RATE      = 1.5     // emit every 40ms
const VEL_MED_RATE       = 0.5     // emit every 80ms
const VEL_SLOW_RATE      = 0.1     // emit every 200ms

function LabelContent({ sectionKey }: { sectionKey: SectionKey }) {
  const t = useTranslations('sectionTracker')
  const name = t(sectionKey)
  const num  = numForKey(sectionKey)
  return (
    <>
      <span style={{ color: 'var(--lime-500)', fontWeight: 500 }}>{name}</span>
      <span style={{ opacity: 0.4 }}>·</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {pad(num)}
        <span style={{ opacity: 0.4, margin: '0 4px' }}>/</span>
        05
      </span>
    </>
  )
}

export default function StickyCounter() {
  // The tracker tracks #hero / #problem / #solution / etc. — sections
  // that only exist on the home page. Bail on every other route so the
  // chip doesn't appear on /tech, /tecnologia, /about, etc.
  const pathname = usePathname()
  if (pathname !== '/') return null

  return <StickyCounterInner />
}

function StickyCounterInner() {
  const containerRef = useRef<HTMLDivElement>(null)
  const crossfadeRef = useRef<HTMLDivElement>(null)
  const particleLayerRef = useRef<HTMLDivElement>(null)

  const [activeKey,    setActiveKey]    = useState<SectionKey>('surface')
  const [displayedKey, setDisplayedKey] = useState<SectionKey>('surface')
  const [outgoingKey,  setOutgoingKey]  = useState<SectionKey | null>(null)
  const [isMobile,     setIsMobile]     = useState(false)

  const reduced = useReducedMotion()
  const particlesEnabled = !reduced && !isMobile

  // Locale from next-intl provider (driven by NEXT_LOCALE cookie)
  const rawLocale = useLocale()
  const locale: 'es' | 'en' = rawLocale === 'en' ? 'en' : 'es'
  const t = useTranslations('sectionTracker')

  // ── Mobile breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handle = (m: MediaQueryList | MediaQueryListEvent) => setIsMobile(m.matches)
    handle(mq)
    mq.addEventListener('change', handle)
    return () => mq.removeEventListener('change', handle)
  }, [])

  // ── ScrollTrigger per tracked section ─────────────────────────────────
  useEffect(() => {
    gsapSetup()
    const triggers: ScrollTrigger[] = []
    SECTION_MAP.forEach(({ id, key }) => {
      const el = document.getElementById(id)
      if (!el) return
      triggers.push(ScrollTrigger.create({
        trigger: el,
        start: 'top 50%',
        end:   'bottom 50%',
        onEnter:     () => setActiveKey(key),
        onEnterBack: () => setActiveKey(key),
      }))
    })
    ScrollTrigger.refresh()
    return () => { triggers.forEach(t => t.kill()) }
  }, [])

  // ─────────────────────────────────────────────────────────────────────
  // PARTICLE TRAIL
  // ─────────────────────────────────────────────────────────────────────

  const particlePoolRef    = useRef<HTMLDivElement[]>([])
  const nextParticleIdxRef = useRef(0)
  const scrollVelocityRef  = useRef(0)
  const lastScrollYRef     = useRef(0)
  const lastTimeRef        = useRef(0)
  const lastEmitTimeRef    = useRef(0)
  const rafRef             = useRef(0)
  const blurActiveRef      = useRef(false)

  // Build the particle pool once when enabled; tear down on disable
  useEffect(() => {
    if (!particlesEnabled) return
    const layer = particleLayerRef.current
    if (!layer) return

    const pool: HTMLDivElement[] = []
    for (let i = 0; i < POOL_SIZE; i++) {
      const p = document.createElement('div')
      p.style.cssText = `
        position: absolute;
        width: 4px; height: 4px;
        background: #9FAF08;
        border-radius: 50%;
        box-shadow: 0 0 6px rgba(159,175,8,0.6);
        pointer-events: none;
        opacity: 0;
        will-change: transform, opacity;
      `
      layer.appendChild(p)
      pool.push(p)
    }
    particlePoolRef.current = pool

    return () => {
      pool.forEach(p => {
        gsap.killTweensOf(p)
        p.remove()
      })
      particlePoolRef.current = []
    }
  }, [particlesEnabled])

  // Pick the next pool particle, place it at the tracker's left edge
  // (with jitter), and tween it up-and-left while fading out.
  const emitParticle = useCallback((opts?: { burst?: boolean }) => {
    const tracker = containerRef.current
    const pool    = particlePoolRef.current
    if (!tracker || pool.length === 0) return

    const r  = tracker.getBoundingClientRect()
    const ex = r.left - 6 + (Math.random() - 0.5) * 8
    const ey = r.top + r.height / 2 + (Math.random() - 0.5) * 12

    const p = pool[nextParticleIdxRef.current]
    nextParticleIdxRef.current = (nextParticleIdxRef.current + 1) % pool.length
    if (!p) return

    gsap.killTweensOf(p)
    if (opts?.burst) {
      // Section-change burst — slightly larger, brighter, more upward
      p.style.boxShadow = '0 0 10px rgba(159,175,8,0.85)'
      gsap.set(p, {
        left: ex, top: ey,
        xPercent: -50, yPercent: -50,
        x: 0, y: 0,
        opacity: 1,
        scale: 1.3 + Math.random() * 0.3,
      })
      gsap.to(p, {
        x: -(50 + Math.random() * 60),
        y: -(40 + Math.random() * 50),
        opacity: 0,
        scale: 0.5,
        duration: 1.2 + Math.random() * 0.6,
        ease: 'power2.out',
        onComplete: () => {
          // Restore default glow for normal scroll emits
          p.style.boxShadow = '0 0 6px rgba(159,175,8,0.6)'
        },
      })
    } else {
      gsap.set(p, {
        left: ex, top: ey,
        xPercent: -50, yPercent: -50,
        x: 0, y: 0,
        opacity: 0.8,
        scale: 0.8 + Math.random() * 0.4,
      })
      gsap.to(p, {
        x: -(40 + Math.random() * 40),
        y: -(20 + Math.random() * 30),
        opacity: 0,
        scale: 0.5,
        duration: 1.0 + Math.random() * 0.8,
        ease: 'power2.out',
      })
    }
  }, [])

  const emitBurst = useCallback((count: number) => {
    for (let i = 0; i < count; i++) emitParticle({ burst: true })
  }, [emitParticle])

  // Track scroll velocity (px/ms, signed — positive = down)
  useEffect(() => {
    if (!particlesEnabled) return
    lastScrollYRef.current = window.scrollY
    lastTimeRef.current    = performance.now()

    const onScroll = () => {
      const now = performance.now()
      const dy  = window.scrollY - lastScrollYRef.current
      const dt  = Math.max(1, now - lastTimeRef.current)
      scrollVelocityRef.current = dy / dt
      lastScrollYRef.current = window.scrollY
      lastTimeRef.current    = now
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [particlesEnabled])

  // rAF loop: dampens velocity, emits at speed-dependent rate, toggles
  // motion blur when scrolling very fast.
  useEffect(() => {
    if (!particlesEnabled) return

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick)
      if (document.hidden) return

      const v = scrollVelocityRef.current

      if (v > 0) {
        let interval = Infinity
        if      (v >= VEL_FAST_RATE) interval = 40
        else if (v >= VEL_MED_RATE)  interval = 80
        else if (v >= VEL_SLOW_RATE) interval = 200

        const now = performance.now()
        if (interval !== Infinity && now - lastEmitTimeRef.current >= interval) {
          emitParticle()
          lastEmitTimeRef.current = now
        }

        // Fast-scroll motion blur on the tracker itself
        const tracker = containerRef.current
        if (tracker) {
          if (v >= VEL_FAST_BLUR && !blurActiveRef.current) {
            blurActiveRef.current = true
            tracker.style.filter = 'blur(0.5px) drop-shadow(-8px 0 6px rgba(159,175,8,0.4))'
          } else if (v < VEL_BLUR_RELEASE && blurActiveRef.current) {
            blurActiveRef.current = false
            tracker.style.filter = ''
          }
        }
      }

      // Friction — particles taper off after scroll stops
      scrollVelocityRef.current *= VEL_DAMPING
      if (Math.abs(scrollVelocityRef.current) < 0.005) scrollVelocityRef.current = 0
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [particlesEnabled, emitParticle])

  // ─────────────────────────────────────────────────────────────────────
  // CROSSFADE TRANSITION (also fires the section-change burst)
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeKey === displayedKey) return

    const el = crossfadeRef.current
    const container = containerRef.current

    if (reduced || !el) {
      setDisplayedKey(activeKey)
      return
    }

    const oldW = el.offsetWidth
    setOutgoingKey(displayedKey)
    setDisplayedKey(activeKey)

    // Fire the burst right away — it travels alongside the crossfade
    if (particlesEnabled) emitBurst(5)

    // After React re-renders with new incoming + outgoing labels
    requestAnimationFrame(() => {
      // Measure natural width of new content
      gsap.set(el, { clearProps: 'width' })
      const newW = el.offsetWidth

      // Freeze at old width, animate to new
      gsap.set(el, { width: oldW })
      gsap.to(el, {
        width: newW,
        duration: 0.4,
        ease: 'power2.inOut',
        onComplete: () => {
          gsap.set(el, { clearProps: 'width' })
          setOutgoingKey(null)
        },
      })

      // Crossfade the two stacked labels
      const outgoingEl = el.querySelector<HTMLElement>('[data-tr-state="out"]')
      const incomingEl = el.querySelector<HTMLElement>('[data-tr-state="in"]')
      if (outgoingEl) gsap.fromTo(outgoingEl, { opacity: 1 }, { opacity: 0, duration: 0.4, ease: 'power2.inOut' })
      if (incomingEl) gsap.fromTo(incomingEl, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.inOut' })

      // Subtle container pulse — anchored to right edge so the chip doesn't drift
      if (container) {
        gsap.fromTo(container,
          { scale: 1 },
          { scale: 1.03, duration: 0.2, yoyo: true, repeat: 1, ease: 'power2.inOut', transformOrigin: '100% 50%' },
        )
      }
    })
  }, [activeKey, displayedKey, reduced, particlesEnabled, emitBurst])

  const displayedNum  = numForKey(displayedKey)
  const displayedName = t(a11yKey(displayedKey))
  const ariaLabel = t('ariaTemplate', { name: displayedName, num: displayedNum })

  return (
    <>
      {/* Particle trail layer — sits just below the tracker, full-viewport
          fixed container that pool particles get appended to.
          aria-hidden because it's decorative. */}
      <div
        ref={particleLayerRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0, left: 0,
          width: '100vw', height: '100vh',
          pointerEvents: 'none',
          zIndex: 49,
        }}
      />

      <div
        ref={containerRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-label={ariaLabel}
        style={{
          // ─── ORIGINAL STYLING PRESERVED VERBATIM ──────────────────────
          position: 'fixed',
          bottom: 28,
          right: 'clamp(32px, 5vw, 64px)',
          zIndex: 50,
          padding: '5px 10px',
          background: 'rgba(20, 14, 4, 0.42)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderRadius: 3,
          border: '1px solid rgba(255,252,240,0.08)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--fs-caption, 10px)',
          fontWeight: 400,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'rgba(241,233,207,0.4)',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      >
        {/* Crossfade wrapper — holds ghost (sizes box), incoming, and outgoing */}
        <div
          ref={crossfadeRef}
          style={{
            position: 'relative',
            display: 'inline-block',
            whiteSpace: 'nowrap',
          }}
        >
          {/* Ghost — invisible, establishes width */}
          <div
            aria-hidden="true"
            style={{
              visibility: 'hidden',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <LabelContent sectionKey={displayedKey} />
          </div>

          {/* Incoming (current) — visible, stacked on ghost */}
          <div
            data-tr-state="in"
            style={{
              position: 'absolute',
              left: 0, top: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              whiteSpace: 'nowrap',
            }}
          >
            <LabelContent sectionKey={displayedKey} />
          </div>

          {/* Outgoing — present only during transition */}
          {outgoingKey && (
            <div
              data-tr-state="out"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 0, top: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                whiteSpace: 'nowrap',
              }}
            >
              <LabelContent sectionKey={outgoingKey} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
